<!-- src/components/Notification.vue -->
<template>
  <Teleport to="body">
    <transition name="slide-fade">
      <div
        v-if="show"
        :class="notificationClasses"
        class="fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-50 max-w-sm cursor-pointer"
        @click="hideNotification"
        @mouseenter="pauseCountdown"
        @mouseleave="resumeCountdown"
      >
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <CheckCircleIcon v-if="localType === 'success'" class="h-5 w-5" />
            <XCircleIcon v-else-if="localType === 'error'" class="h-5 w-5" />
            <InfoIcon v-else-if="localType === 'info'" class="h-5 w-5" />
            <TriangleAlert v-else-if="localType === 'warning'" class="h-5 w-5" />
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-medium">{{ localMessage }}</p>
          </div>
          <div class="ml-4 flex-shrink-0">
            <button
              type="button"
              class="inline-flex text-white hover:opacity-75 focus:outline-none"
              @click.stop="hideNotification"
            >
              <XIcon class="h-4 w-4" />
            </button>
          </div>
        </div>

        <!-- Barre de progression -->
        <div
          class="absolute bottom-0 left-0 h-1 bg-white bg-opacity-30 rounded-b-lg overflow-hidden"
        >
          <div
            class="h-full bg-white transition-all duration-75 ease-linear"
            :style="{ width: progressBarWidth + '%' }"
          ></div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { CheckCircleIcon, InfoIcon, TriangleAlert, XCircleIcon, XIcon } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import type { NotificationType } from '../../types/notification'

// État local
const show = ref(false)
const localMessage = ref('')
const localType = ref<NotificationType>('success')
const progressBarWidth = ref(100)
const intervalId = ref<ReturnType<typeof setInterval> | null>(null)
const countdown = ref(4000) // 4 secondes par défaut

// Classes computées
const notificationClasses = computed(() => {
  const baseClasses = 'notification'
  const typeClasses = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-blue-500 text-white',
    warning: 'bg-yellow-500 text-white',
  }

  return [baseClasses, typeClasses[localType.value]]
})

// Méthodes
const showNotification = (
  message: string,
  type: NotificationType = 'success',
  duration: number = 4000
): void => {
  // Cacher la notification précédente si elle existe
  hideNotification()

  localMessage.value = message
  localType.value = type
  countdown.value = duration
  show.value = true
  progressBarWidth.value = 100

  startCountdown()
}

const hideNotification = (): void => {
  show.value = false
  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }
  progressBarWidth.value = 100
}

const startCountdown = (): void => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }

  const step = 100 / (countdown.value / 50) // Mise à jour toutes les 50ms
  intervalId.value = setInterval(() => {
    progressBarWidth.value -= step
    if (progressBarWidth.value <= 0) {
      hideNotification()
    }
  }, 50)
}

const pauseCountdown = (): void => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }
}

const resumeCountdown = (): void => {
  if (!intervalId.value && show.value && progressBarWidth.value > 0) {
    startCountdown()
  }
}

// Exposer les méthodes pour le composant parent
defineExpose({
  showNotification,
  hideNotification,
})
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.notification {
  backdrop-filter: blur(8px);
}
</style>
